import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { adapter } from "@/services/db";
import { inr, text } from "@/lib/format";
import { CategoriesTab } from "@/components/catalog/CategoriesTab";
import { ProductEditor } from "@/components/catalog/ProductEditor";
import { mediaFromDoc } from "@/components/catalog/MediaManager";
import { ProductCategoriesTab } from "@/components/catalog/ProductCategoriesTab";
import { StoreRulesTab } from "@/components/catalog/ShopRules";
import { ProCategoriesTab } from "@/components/catalog/ProCategoriesTab";
import { BulkBar, SelectAllBox, SelectBox, useSelection } from "@/components/shared/BulkSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { productCategoryOf, sortCats } from "@/lib/productCategories";
import { cn } from "@/lib/utils";

const errMsg = (e) => (e?.code === "permission-denied" ? "Permission denied — admin role required" : e?.response?.data?.detail || e?.message);
const discountOf = (mrp, sale) => (mrp > 0 && sale >= 0 ? Math.max(0, Math.round(((mrp - sale) / mrp) * 100)) : 0);
const PROD_HEADERS = ["category", "name", "mrp", "sale_price", "discount", "variants", "bulk_pricing", "product_url", "product_category"];


// Short selling-rules note under a product's name (stock, minimum, installation, tax).
function rulesLine(p, fam) {
  const out = [];
  const docs = fam.length ? fam : [p];
  const tracked = docs.filter((x) => x.track_inventory === true || (x.track_inventory == null && typeof x.stock === "number"));
  if (tracked.length) { const st = tracked.reduce((n, x) => n + (Number(x.stock) || 0), 0); out.push(st <= 0 ? "out of stock" : `stock ${st}`); }
  if (Number(p.moq) > 1) out.push(`min ${p.moq}`);
  if (p.installation_charge != null && p.installation_charge !== "") out.push(Number(p.installation_charge) > 0 ? `install ₹${p.installation_charge}` : "no install");
  if (Number(p.delivery_charge) > 0) out.push(`+₹${p.delivery_charge} delivery`);
  if (p.hsn_code || (p.gst_rate != null && p.gst_rate !== "")) out.push([p.hsn_code && `HSN ${p.hsn_code}`, p.gst_rate != null && p.gst_rate !== "" && `${p.gst_rate}%`].filter(Boolean).join(" "));
  return out.join(" · ");
}

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "categories";
  const categories = useCollection("categories");
  const products = useCollection("products");
  const pcats = useCollection("product_categories");
  const procats = useCollection("professional_categories");
  const [pcatF, setPcatF] = useState("all");
  const [editing, setEditing] = useState(null); // product doc being edited (null = new)
  const [prodOpen, setProdOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const importRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);

  const catNames = useMemo(() => categories.map((c) => c.name || c.id).filter(Boolean), [categories]);

  // ---- products ----
  const openNewProd = () => { setEditing(null); setProdOpen(true); };
  const openEditProdFull = (p) => { setEditing(p); setProdOpen(true); };
  // One table row per product family (variants collapsed under their main listing).
  const productRows = useMemo(() => {
    const groups = {};
    products.forEach((p) => { if (p.variant_group_id) (groups[p.variant_group_id] ||= []).push(p); });
    const seen = new Set();
    const out = [];
    products.forEach((p) => {
      if (!showInactive && p.status === "inactive") return;
      if (!p.variant_group_id) { out.push({ p, fam: [] }); return; }
      if (seen.has(p.variant_group_id)) return;
      const fam = groups[p.variant_group_id].filter((x) => showInactive || x.status !== "inactive");
      if (!fam.length) return;
      seen.add(p.variant_group_id);
      out.push({ p: fam.find((x) => x.variant_primary) || fam[0], fam });
    });
    return out
      .map((r) => ({ ...r, id: r.p.id, pcat: productCategoryOf(r.p, pcats), ids: r.fam.length ? r.fam.map((x) => x.id) : [r.p.id], hidden: r.fam.length ? r.fam.every((x) => x.hidden) : !!r.p.hidden }))
      .filter((r) => pcatF === "all" || (pcatF === "__none" ? !r.pcat : pcatF === "__hidden" ? r.hidden : r.pcat === pcatF));
  }, [products, showInactive, pcats, pcatF]);
  const pickP = useSelection(productRows);
  const pcatNames = useMemo(() => [...pcats].sort(sortCats).map((c) => c.name), [pcats]);
  const setHiddenRow = async (r, hidden) => {
    try { await adapter.batchPatchFields("products", r.ids, { hidden, updated_at: new Date().toISOString() }); toast.success(`${r.p.variant_base_name || r.p.name} ${hidden ? "hidden from" : "shown on"} brugo.in`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const prodActions = [
    { key: "hide", label: "Hide on brugo.in", icon: "EyeOff", eligible: (r) => !r.hidden, skipNote: "already hidden", description: "They disappear from the shop and can't be ordered. Nothing is deleted — show them again any time.",
      run: (r) => adapter.batchPatchFields("products", r.ids, { hidden: true, updated_at: new Date().toISOString() }) },
    { key: "show", label: "Show on brugo.in", icon: "Eye", eligible: (r) => r.hidden, skipNote: "already showing",
      run: (r) => adapter.batchPatchFields("products", r.ids, { hidden: false, updated_at: new Date().toISOString() }) },
    { key: "pcat", label: "Set product category", icon: "Tags", description: "Every variant of each selected product moves under this shop heading.",
      ask: [{ key: "pcat", label: "Product category", type: "select", required: true, options: pcatNames.map((n) => [n, n]) }],
      run: (r, v) => adapter.batchPatchFields("products", r.ids, { shop_category: v.pcat, updated_at: new Date().toISOString() }) },
    { key: "kind", label: "Set type", icon: "Boxes", description: "Raw materials show under their product category on brugo.in; finished goods show in the Finished goods tab.",
      ask: [{ key: "kind", label: "Type", type: "select", required: true, options: [["raw", "Raw material"], ["finished", "Finished goods"]] }],
      run: (r, v) => adapter.batchPatchFields("products", r.ids, { kind: v.kind, updated_at: new Date().toISOString() }) },
    { key: "service", label: "Set related service", icon: "Wrench",
      ask: [{ key: "svc", label: "Service", type: "select", required: true, options: catNames.map((n) => [n, n]) }],
      run: (r, v) => adapter.batchPatchFields("products", r.ids, { category: v.svc, updated_at: new Date().toISOString() }) },
    { key: "rules", label: "Set selling rules", icon: "SlidersHorizontal",
      description: "Only the boxes you fill are changed. Installation: 0 = not needed. Stock is per product — set it in the editor.",
      ask: [
        { key: "moq", label: "Minimum order quantity", type: "number", placeholder: "leave blank = no change" },
        { key: "inst", label: "Installation charge ₹ (per unit)", type: "number", placeholder: "leave blank = no change" },
        { key: "del", label: "Delivery charge ₹ (per unit)", type: "number", placeholder: "leave blank = no change" },
        { key: "hsn", label: "HSN code", type: "text", placeholder: "leave blank = no change" },
        { key: "gst", label: "GST rate", type: "select", options: [["__nc", "No change"], ["0", "0%"], ["5", "5%"], ["18", "18%"], ["40", "40%"]] },
      ],
      run: (r, v) => {
        const patch = {};
        if (v.moq !== undefined && v.moq !== "") patch.moq = Math.max(1, Math.trunc(Number(v.moq) || 1));
        if (v.inst !== undefined && v.inst !== "") Object.assign(patch, { installation_charge: Math.max(0, Number(v.inst) || 0), installation_charge_type: "per_unit" });
        if (v.del !== undefined && v.del !== "") Object.assign(patch, { delivery_charge: Math.max(0, Number(v.del) || 0) || null, delivery_charge_type: "per_unit" });
        if (v.hsn) patch.hsn_code = String(v.hsn).trim();
        if (v.gst !== undefined && v.gst !== "" && v.gst !== "__nc") patch.gst_rate = Number(v.gst);
        if (!Object.keys(patch).length) return Promise.resolve();
        return adapter.batchPatchFields("products", r.ids, { ...patch, updated_at: new Date().toISOString() });
      } },
  ];
  const remove = async (coll, id) => { try { await adapter.remove(coll, id); toast.success("Removed"); } catch (e) { toast.error(errMsg(e)); } };

  // ---- CSV ----
  const exportCSV = () => {
    const esc = (s) => { s = String(s ?? ""); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lines = [PROD_HEADERS.join(",")];
    products.forEach((r) => lines.push([r.category, r.name, r.mrp, r.sale_price, r.discount, (r.variants || []).join("|"), (r.bulk_pricing || []).map((b) => `${b.min_qty}:${b.price}`).join("|"), r.product_url, productCategoryOf(r, pcats) || ""].map(esc).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `brugo_products_${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${products.length} products`);
  };

  // ---- CSV import (bulk products) ----
  const parseCSV = (raw) => {
    const rows = [];
    let field = "", row = [], inQ = false;
    const s = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    for (let i = 0; i < s.length; i++) {
      const c = s[i];
      if (inQ) {
        if (c === '"') { if (s[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
        else field += c;
      } else if (c === '"') inQ = true;
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else field += c;
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter((r) => r.some((x) => String(x).trim() !== ""));
  };

  const rowToProduct = (obj) => {
    const mrp = Number(obj.mrp) || 0, sale = Number(obj.sale_price) || 0;
    const discount = obj.discount === "" || obj.discount == null ? discountOf(mrp, sale) : Number(obj.discount) || 0;
    const variants = String(obj.variants || "").split("|").map((v) => v.trim()).filter(Boolean);
    const bulk_pricing = String(obj.bulk_pricing || "").split("|").map((t) => t.trim()).filter(Boolean).map((t) => {
      const [q, p] = t.split(":"); return { min_qty: Number(q) || 0, price: Number(p) || 0 };
    }).filter((b) => b.min_qty > 0 && b.price > 0);
    return {
      name: String(obj.name || "").trim(), category: String(obj.category || "").trim(),
      mrp, sale_price: sale, discount, variants, bulk_pricing,
      product_url: String(obj.product_url || "").trim(), price: sale || mrp, status: "active",
      ...(String(obj.product_category || "").trim() ? { shop_category: String(obj.product_category).trim() } : {}),
    };
  };

  const onImportFile = async (file) => {
    if (!file) return;
    try {
      const rows = parseCSV(await file.text());
      if (rows.length < 2) return toast.error("CSV has no data rows");
      const header = rows[0].map((h) => h.trim().toLowerCase());
      if (!header.includes("name")) return toast.error("CSV must include a 'name' column");
      const parsed = [], errs = [];
      rows.slice(1).forEach((cols, idx) => {
        const obj = {}; header.forEach((h, i) => { obj[h] = cols[i]; });
        const p = rowToProduct(obj);
        if (!p.name) { errs.push(`Row ${idx + 2}: missing name — skipped`); return; }
        parsed.push(p);
      });
      setImportRows(parsed); setImportErrors(errs); setImportOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
    finally { if (importRef.current) importRef.current.value = ""; }
  };

  const runImport = async () => {
    if (!importRows.length) return;
    setImporting(true);
    try {
      const now = new Date().toISOString();
      const created = await adapter.bulkAdd("products", importRows.map((p) => ({ ...p, created_at: now, updated_at: now })));
      toast.success(`Imported ${created} products`);
      setImportOpen(false); setImportRows([]); setImportErrors([]);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const sample = [
      PROD_HEADERS.join(","),
      'Electrical,LED Panel Light 18W,299,199,,Cool White|Warm White,10:180|50:160,https://example.com/led,Switches & Sockets',
      'Plumbing,PVC Pipe 1inch,150,120,20,,25:110|100:100,,Plumbing Pipes, Fittings & Tanks',
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "brugo_products_template.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader title="Catalog & Services" subtitle="Services (Plumbing, Painting…), products / materials, and product categories — the shop headings (Tiles, Taps, Lights…). Hide anything from brugo.in with the eye switch; written live to the shared Firestore collections." />

      <Tabs value={tab === "pro_categories" ? "categories" : tab} onValueChange={(v) => setParams({ tab: v })} className="mb-4">
        <TabsList>
          <TabsTrigger value="categories" data-testid="cat-tab-categories">Services & professional categories</TabsTrigger>
          <TabsTrigger value="products" data-testid="cat-tab-products">Products / Materials</TabsTrigger>
          <TabsTrigger value="product_categories" data-testid="cat-tab-pcats">Product categories ({pcats.length})</TabsTrigger>
          <TabsTrigger value="store_rules" data-testid="cat-tab-store">Store rules</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "categories" || tab === "pro_categories" ? (
        <div className="space-y-4">
          <CategoriesTab categories={categories} />
          <ProCategoriesTab procats={procats} categories={categories} />
        </div>
      ) : tab === "store_rules" ? (
        <StoreRulesTab />
      ) : tab === "product_categories" ? (
        <ProductCategoriesTab cats={pcats} products={products} onShowUncategorised={() => { setPcatF("__none"); setParams({ tab: "products" }); }} />
      ) : (
        <Card>
          <div className="p-4 border-b border-border flex flex-wrap justify-end gap-2">
            <Select value={pcatF} onValueChange={setPcatF}>
              <SelectTrigger className="h-9 w-56 text-xs mr-auto" data-testid="product-pcat-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all" className="text-xs">All product categories</SelectItem>
                <SelectItem value="__none" className="text-xs">No product category</SelectItem>
                <SelectItem value="__hidden" className="text-xs">Hidden on brugo.in</SelectItem>
                {pcatNames.map((n) => <SelectItem key={n} value={n} className="text-xs">{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <input ref={importRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="import-csv-input" onChange={(e) => onImportFile(e.target.files[0])} />
            <Button size="sm" variant="ghost" className="gap-1.5" onClick={downloadTemplate} data-testid="template-csv-btn"><Icons.FileDown className="h-4 w-4" /> Template</Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => importRef.current?.click()} data-testid="import-csv-btn"><Icons.Upload className="h-4 w-4" /> Import CSV</Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={exportCSV} data-testid="export-csv-btn"><Icons.Download className="h-4 w-4" /> Export CSV</Button>

            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogContent data-testid="import-preview-dialog" className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">Bulk import products</DialogTitle></DialogHeader>
                <div className="space-y-3 py-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 font-medium" data-testid="import-valid-count"><Icons.CheckCircle2 className="h-3.5 w-3.5" /> {importRows.length} ready</span>
                    {importErrors.length > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 font-medium"><Icons.AlertTriangle className="h-3.5 w-3.5" /> {importErrors.length} skipped</span>}
                  </div>
                  {importRows.length > 0 && (
                    <div className="rounded-lg border border-border max-h-64 overflow-auto">
                      <Table>
                        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Product category</TableHead><TableHead>MRP</TableHead><TableHead>Sale</TableHead><TableHead>Disc.</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {importRows.slice(0, 50).map((p, i) => (
                            <TableRow key={i} data-testid={`import-row-${i}`}>
                              <TableCell className="font-medium text-foreground">{text(p.name)}</TableCell>
                              <TableCell>{text(p.shop_category || p.category)}</TableCell>
                              <TableCell className="font-mono text-muted-foreground">{p.mrp ? inr(p.mrp) : "—"}</TableCell>
                              <TableCell className="font-mono text-foreground">{inr(p.sale_price || p.price)}</TableCell>
                              <TableCell className="text-emerald-600 text-sm">{p.discount ? `${p.discount}%` : "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {importRows.length > 50 && <p className="text-xs text-muted-foreground px-3 py-2">…and {importRows.length - 50} more</p>}
                    </div>
                  )}
                  {importErrors.length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-xs text-amber-800 max-h-24 overflow-auto space-y-0.5">
                      {importErrors.slice(0, 10).map((e, i) => <p key={i}>{e}</p>)}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Columns: <span className="font-mono">{PROD_HEADERS.join(", ")}</span>. Variants separated by <span className="font-mono">|</span>, bulk tiers as <span className="font-mono">minQty:price</span> separated by <span className="font-mono">|</span>. New products are added (existing ones are not modified).</p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setImportOpen(false)} data-testid="import-cancel">Cancel</Button>
                  <Button onClick={runImport} disabled={importing || importRows.length === 0} data-testid="import-confirm">{importing ? "Importing…" : `Import ${importRows.length} products`}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button size="sm" variant={showInactive ? "secondary" : "ghost"} className="gap-1.5" onClick={() => setShowInactive(!showInactive)} data-testid="show-inactive-products"><Icons.Archive className="h-4 w-4" />{showInactive ? "Hide" : "Show"} retired</Button>
            <Button size="sm" className="gap-1.5" onClick={openNewProd} data-testid="add-product-btn"><Icons.Plus className="h-4 w-4" /> Add Product</Button>
            <ProductEditor open={prodOpen} onClose={() => setProdOpen(false)} initial={editing} products={products} catNames={catNames} prodCatDocs={pcats} />
          </div>
          <div className="px-3 pt-3"><BulkBar s={pickP} path="products" rows={productRows} label="products" actions={prodActions} nameOf={(r) => r.p.variant_base_name || r.p.name || r.id} /></div>
          <Table>
            <TableHeader><TableRow><TableHead className="w-8"><SelectAllBox s={pickP} testId="product-select-all" /></TableHead><TableHead>Product</TableHead><TableHead>Product category</TableHead><TableHead>MRP</TableHead><TableHead>Sale</TableHead><TableHead>Disc.</TableHead><TableHead>Variants</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {productRows.map((row) => {
                const { p, fam } = row;
                const media = mediaFromDoc(p);
                const cover = media.find((m) => m.type !== "video");
                const vids = media.filter((m) => m.type === "video").length;
                const prices = fam.map((x) => Number(x.sale_price ?? x.price) || 0).filter(Boolean);
                const lo = prices.length ? Math.min(...prices) : null, hi = prices.length ? Math.max(...prices) : null;
                return (
                <TableRow key={p.id} className={cn((p.status === "inactive" || row.hidden) && "opacity-50", pickP.sel.has(p.id) && "bg-primary/5")} data-testid={`product-row-${p.id}`}>
                  <TableCell><SelectBox s={pickP} id={p.id} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 shrink-0 rounded-md border border-border bg-secondary/50 overflow-hidden flex items-center justify-center">
                        {cover ? <img src={cover.url} alt="" className="h-full w-full object-cover" /> : <Icons.ImageOff className="h-4 w-4 text-muted-foreground" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate max-w-[260px]">{text(fam.length ? p.variant_base_name || p.name : p.name || p.id)}</p>
                        <p className="text-[11px] text-muted-foreground">{media.length - vids} photo{media.length - vids === 1 ? "" : "s"}{vids ? ` · ${vids} video${vids > 1 ? "s" : ""}` : ""}{p.status === "inactive" ? " · retired" : ""}{row.hidden ? " · hidden on brugo.in" : ""}{rulesLine(p, fam) ? ` · ${rulesLine(p, fam)}` : ""}{p.product_url && <> · <a href={p.product_url} target="_blank" rel="noreferrer" className="font-mono text-primary hover:underline">link ↗</a></>}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{String(p.kind || "").toLowerCase() === "finished" || p.is_finished ? <span className="mr-1 text-[10px] rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5">Finished</span> : null}{row.pcat ? <span className="text-sm">{row.pcat}</span> : <span className="text-xs text-amber-700" title="Raw materials without a product category show under “Other materials” on brugo.in">None yet</span>}{p.category ? <span className="block text-[11px] text-muted-foreground">Service: {p.category}</span> : null}</TableCell>
                  <TableCell className="font-mono text-muted-foreground line-through">{p.mrp ? inr(p.mrp) : "—"}</TableCell>
                  <TableCell className="font-mono text-foreground whitespace-nowrap">{fam.length > 1 && lo !== hi ? `${inr(lo)} – ${inr(hi)}` : inr(p.sale_price ?? p.price)}</TableCell>
                  <TableCell className="text-emerald-600 text-sm">{p.discount ? `${p.discount}%` : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[260px]">
                    {fam.length
                      ? <div className="flex flex-wrap gap-1"><span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 font-medium">{fam.length} variant{fam.length > 1 ? "s" : ""}</span>{fam.slice(0, 4).map((x) => <span key={x.id} className="rounded-full border border-border px-2 py-0.5">{x.variant_label}</span>)}{fam.length > 4 && <span>+{fam.length - 4}</span>}</div>
                      : (Array.isArray(p.variants) && p.variants.length ? p.variants.join(", ") : "—")}
                    {Array.isArray(p.bulk_pricing) && p.bulk_pricing.length ? <span className="block mt-1">{p.bulk_pricing.length} bulk tiers</span> : null}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title={row.hidden ? "Show on brugo.in" : "Hide on brugo.in"} onClick={() => setHiddenRow(row, !row.hidden)} data-testid={`toggle-visible-${p.id}`}>{row.hidden ? <Icons.EyeOff className="h-4 w-4 text-muted-foreground" /> : <Icons.Eye className="h-4 w-4" />}</Button>
                    <EditRecordButton path="products" id={p.id} className="h-8 w-8" />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditProdFull(p)} data-testid={`edit-product-${p.id}`}><Icons.Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => (fam.length > 1 ? toast.error("This product has variants — open it and switch variants off, or delete them one by one in Data Explorer") : remove("products", p.id))} data-testid={`remove-product-${p.id}`}><Icons.Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
                );
              })}
              {productRows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No products yet. Add one.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
